import { useCallback, useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { api } from "../utils/api";
import { useRunStore } from "../state/runStore";
import { useWeekCars, CarSwitcherBar, carLabel } from "../components/CarSwitcher";

interface ProfileSummary {
  carId: string;
  displayName: string;
  verified: boolean;
  enabledLevers: string[];
  primaryLever: string | null;
  applicableTrackBuckets: string[];
}

interface AdjState {
  lrOffset: number | null;
  rrOffset: number | null;
  lfPressure: number | null;
  lrPressure: number | null;
}

interface StopRecord {
  stopNumber: number;
  complaintSlider: number;
  complaintLocation: string;
  fuel: number | null;
  stateBefore: AdjState;
  recommendedDelta: AdjState;
  stateAfter: AdjState;
  confidence: string;
  why: string | null;
  feedbackOutcome: string | null;
  feedbackNote: string | null;
  createdAt: string | null;
}

interface Session {
  id: string;
  carId: string;
  trackWeekId: string | null;
  baselineState: AdjState;
  currentState: AdjState;
  bestKnownState: AdjState | null;
  stopCount: number;
  isActive: boolean;
  stops: StopRecord[];
}

interface Recommendation {
  shouldAdjust: boolean;
  delta: AdjState;
  targetState: AdjState;
  confidence: string;
  why: string;
  fallback: string | null;
  recommendedLevers: string[];
}

const LOCATIONS = [
  { value: "entry", label: "Entry" },
  { value: "center", label: "Center" },
  { value: "exit", label: "Exit" },
  { value: "allAround", label: "All around" },
] as const;

const TRACK_BUCKETS = [
  { value: "short_track", label: "Short track" },
  { value: "intermediate", label: "Intermediate" },
  { value: "superspeedway", label: "Superspeedway" },
  { value: "flat_track", label: "Flat track" },
  { value: "road_course", label: "Road course" },
] as const;

// Humanized pit-road lever names (match StateDisplay's labels). The engine's
// internal keys (lrOffset, lfPressure…) are jargon — never surface them raw.
const LEVER_LABELS: Record<string, string> = {
  lrOffset: "LR offset",
  rrOffset: "RR offset",
  lfPressure: "LF pressure",
  rfPressure: "RF pressure",
  lrPressure: "LR pressure",
  rrPressure: "RR pressure",
};
const leverLabel = (k: string): string => LEVER_LABELS[k] ?? k;

// Confidence -> editorial pill modifier (no gamey colors; uses the flag/live system).
// Low confidence is muted, not danger-red: red is reserved for "worse"/errors, so a
// low-but-correct call doesn't read as a dangerous adjustment.
const confidencePill = (c: string): string =>
  c === "high" ? "pill--success" : c === "medium" ? "pill--warning" : "pill--quiet";

// Map a week's iRacing series to the pit-profile car id (the launch trio).
function carIdForSeries(seriesName: string | null | undefined): string | null {
  const s = (seriesName || "").toLowerCase();
  if (s.includes("class c") || s.includes("truck")) return "nascar-trucks";
  if (s.includes("class b") || s.includes("xfinity") || s.includes("o'reilly") || s.includes("oreilly")) return "nascar-oreilly";
  if (s.includes("class a") || s.includes("cup")) return "nascar-cup-next-gen";
  return null;
}

export default function PitAdjustments() {
  const { trackWeekId } = useParams<{ trackWeekId: string }>();
  const trackWeeks = useRunStore((s) => s.trackWeeks);

  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [profilesError, setProfilesError] = useState(false);
  const [selectedCar, setSelectedCar] = useState("");
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Complaint inputs
  const [slider, setSlider] = useState(0);
  const [location, setLocation] = useState("allAround");
  const [fuel, setFuel] = useState("16");
  const [trackBucket, setTrackBucket] = useState("short_track");
  const [note, setNote] = useState("");

  // Last recommendation (for display before committing)
  const [lastRec, setLastRec] = useState<Recommendation | null>(null);

  // Per-car switcher (#279): only materializes on multi-car custom weeks.
  const { cars, multi, selectedCarId: switcherCar, setCar } = useWeekCars(trackWeekId);

  useEffect(() => {
    api<ProfileSummary[]>("/api/strategy/pit-adjustment/profiles")
      .then(setProfiles)
      .catch(() => setProfilesError(true))
      .finally(() => setProfilesLoading(false));
  }, []);

  // Reset the resolved car when the week changes: the :trackWeekId route param
  // changes without remounting this component, so a stale car would otherwise
  // leak from week A into week B (selectedCar is unkeyed local state).
  useEffect(() => {
    setSelectedCar("");
    setSession(null);
  }, [trackWeekId]);

  // Multi-car week: the switcher IS the selection.
  useEffect(() => {
    if (multi && switcherCar && switcherCar !== selectedCar) setSelectedCar(switcherCar);
  }, [multi, switcherCar, selectedCar]);

  // Single-car / synced week: resolve the car once from the week's actual run
  // (preferred) or the series, before the user touches anything. No dropdown.
  useEffect(() => {
    if (multi || selectedCar || !trackWeekId) return;
    const tw = trackWeeks.find((w) => w.id === trackWeekId);
    const resolved = cars[0]?.carId || carIdForSeries(tw?.seriesName);
    if (resolved) setSelectedCar(resolved);
  }, [multi, cars, trackWeeks, trackWeekId, selectedCar]);

  useEffect(() => {
    if (!selectedCar || !trackWeekId) return;
    api<Session>(
      `/api/strategy/pit-adjustment/sessions/active?carId=${selectedCar}&trackWeekId=${trackWeekId}`,
    )
      .then(setSession)
      .catch(() => setSession(null));
  }, [selectedCar, trackWeekId]);

  const createSession = useCallback(async () => {
    if (!selectedCar) return;
    setLoading(true);
    setError(null);
    try {
      const s = await api<Session>("/api/strategy/pit-adjustment/sessions", {
        method: "POST",
        body: JSON.stringify({ carId: selectedCar, trackWeekId: trackWeekId || null }),
      });
      setSession({ ...s, stops: [] });
    } catch (e: any) {
      setError(e.message || "Failed to create session");
    } finally {
      setLoading(false);
    }
  }, [selectedCar, trackWeekId]);

  const submitStop = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    setLastRec(null);
    try {
      const resp = await api<{
        stopNumber: number;
        recommendation: Recommendation;
        sessionState: AdjState;
      }>(`/api/strategy/pit-adjustment/sessions/${session.id}/stops`, {
        method: "POST",
        body: JSON.stringify({
          complaintSlider: slider,
          complaintLocation: location,
          complaintNote: note || null,
          fuel: parseFloat(fuel) || 16,
          trackBucket,
          sessionContext: "race",
          runIntent: "balanced",
          tireChange: "four",
        }),
      });
      setLastRec(resp.recommendation);
      const updated = await api<Session>(`/api/strategy/pit-adjustment/sessions/${session.id}`);
      setSession(updated);
    } catch (e: any) {
      setError(e.message || "Failed to get recommendation");
    } finally {
      setLoading(false);
    }
  }, [session, slider, location, fuel, trackBucket, note]);

  const submitFeedback = useCallback(
    async (stopNumber: number, outcome: string) => {
      if (!session) return;
      try {
        const resp = await api<{ ok: boolean; bestKnownState: AdjState | null }>(
          `/api/strategy/pit-adjustment/sessions/${session.id}/stops/${stopNumber}/feedback`,
          { method: "POST", body: JSON.stringify({ outcome }) },
        );
        setSession((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            bestKnownState: resp.bestKnownState,
            stops: prev.stops.map((s) =>
              s.stopNumber === stopNumber ? { ...s, feedbackOutcome: outcome } : s,
            ),
          };
        });
      } catch {
        // silent — non-critical
      }
    },
    [session],
  );

  const rollbackToBest = useCallback(async () => {
    if (!session) return;
    try {
      const resp = await api<{ currentState: AdjState }>(
        `/api/strategy/pit-adjustment/sessions/${session.id}/rollback-to-best`,
        { method: "POST" },
      );
      setSession((prev) => (prev ? { ...prev, currentState: resp.currentState } : prev));
    } catch (e: any) {
      setError(e.message);
    }
  }, [session]);

  const closeSession = useCallback(async () => {
    if (!session) return;
    try {
      await api(`/api/strategy/pit-adjustment/sessions/${session.id}/close`, { method: "POST" });
      setSession(null);
      setLastRec(null);
    } catch {
      // silent
    }
  }, [session]);

  const sliderLabel =
    slider === 0 ? "Neutral" : slider < 0 ? `Tight (${Math.abs(slider)})` : `Loose (${slider})`;

  const selectedProfile = profiles.find((p) => p.carId === selectedCar);
  const availableBuckets = selectedProfile
    ? TRACK_BUCKETS.filter((b) => selectedProfile.applicableTrackBuckets.includes(b.value))
    : TRACK_BUCKETS;

  return (
    <div className="fade-in" style={{ maxWidth: 760 }}>
      {/* Tab content under the WeekWorkspace hero — no page title (the hero owns it). */}
      <span className="eyebrow">Pit balance adjustments</span>

      {/* Car: a switcher only when the week runs 2+ cars; otherwise the single
          car is auto-resolved and shown as a static label (no dropdown). */}
      <div className="card" style={st.block}>
        {multi ? (
          <CarSwitcherBar
            cars={cars}
            selectedCarId={selectedCar || undefined}
            setCar={(id) => {
              setCar(id);
              setSession(null);
              setLastRec(null);
              const p = profiles.find((pr) => pr.carId === id);
              if (p && p.applicableTrackBuckets.length > 0 && !p.applicableTrackBuckets.includes(trackBucket)) {
                setTrackBucket(p.applicableTrackBuckets[0]);
              }
            }}
          />
        ) : (
          <div className="section-label" style={{ margin: 0 }}>
            Car
            {selectedProfile ? ` · ${selectedProfile.displayName}` : cars[0] ? ` · ${carLabel(cars[0])}` : ""}
          </div>
        )}
        {profilesLoading && (
          <div style={st.hint}>
            <span className="spinner" style={{ display: "inline-block", verticalAlign: "middle" }} />
          </div>
        )}
        {profilesError && <div style={{ ...st.hint, color: "var(--flag-red)" }}>Couldn't load car profiles.</div>}
        {selectedProfile && (
          <div style={st.profileMeta}>
            <span className={`pill ${selectedProfile.verified ? "pill--success" : "pill--quiet"}`}>
              {selectedProfile.verified ? "Verified profile" : "Unverified"}
            </span>
            <span style={st.leverHint}>
              Adjusts {selectedProfile.enabledLevers.map(leverLabel).join(" · ")}
            </span>
          </div>
        )}
        {!selectedProfile && selectedCar && !profilesLoading && (
          <div style={st.hint}>
            No pit profile for this car. Fuel planning still works.
          </div>
        )}
      </div>

      {selectedCar && selectedProfile && !session && (
        <button onClick={createSession} disabled={loading} className="btn btn-primary">
          {loading ? "Starting…" : "Start new session"}
        </button>
      )}

      {error && (
        <div className="card" style={st.error}>
          {error}
        </div>
      )}

      {session && (
        <>
          {/* Session header */}
          <div className="card" style={st.sessionHeader}>
            <span style={st.sessionLabel}>
              Session · {session.stopCount} stop{session.stopCount !== 1 ? "s" : ""}
            </span>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {session.bestKnownState && (
                <button onClick={rollbackToBest} className="btn btn-secondary" style={st.btnSm}>
                  Return to best
                </button>
              )}
              <button onClick={closeSession} className="btn btn-ghost" style={st.btnSmDanger}>
                End session
              </button>
            </div>
          </div>

          {/* Current / best state */}
          <div className="card" style={st.block}>
            <div className="section-label">Current state</div>
            <StateDisplay state={session.currentState} />
            {session.bestKnownState && (
              <>
                <div className="section-label" style={{ marginTop: "0.85rem" }}>
                  Best known state
                </div>
                <StateDisplay state={session.bestKnownState} />
              </>
            )}
          </div>

          {/* Complaint input */}
          {session.isActive && (
            <div className="card" style={{ ...st.block, display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div className="section-label">Complaint</div>

              <div style={st.row}>
                <label style={st.rowLabel}>Balance</label>
                <div style={{ flex: 1 }}>
                  <input
                    type="range"
                    aria-label="Balance"
                    min={-4}
                    max={4}
                    value={slider}
                    onChange={(e) => { setSlider(parseInt(e.target.value)); setLastRec(null); }}
                    style={{ width: "100%" }}
                  />
                  <div style={st.sliderEnds}>
                    <span>Tight</span>
                    <span>Loose</span>
                  </div>
                  <div style={st.sliderLabel}>{sliderLabel}</div>
                </div>
              </div>

              <div style={st.row}>
                <label style={st.rowLabel}>Location</label>
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  {LOCATIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => { setLocation(value); setLastRec(null); }}
                      className="chip"
                      style={location === value ? st.chipActive : { cursor: "pointer" }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={st.row}>
                <label style={st.rowLabel}>Track type</label>
                <select
                  className="select"
                  value={trackBucket}
                  onChange={(e) => { setTrackBucket(e.target.value); setLastRec(null); }}
                >
                  {availableBuckets.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div style={st.row}>
                <label style={st.rowLabel}>Fuel (gal)</label>
                <input
                  type="number"
                  className="input"
                  value={fuel}
                  onChange={(e) => { setFuel(e.target.value); setLastRec(null); }}
                  min={1}
                  max={50}
                  step={0.5}
                  style={{ width: 110 }}
                />
              </div>

              <div style={st.row}>
                <label style={st.rowLabel}>Note</label>
                <input
                  type="text"
                  className="input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note…"
                  style={{ flex: 1 }}
                />
              </div>

              <button onClick={submitStop} disabled={loading} className="btn btn-primary">
                {loading ? "Calculating…" : "Get recommendation"}
              </button>
            </div>
          )}

          {/* Latest recommendation */}
          {lastRec && (
            <div className="card" style={st.recCard}>
              <div style={st.recTitle}>
                {lastRec.shouldAdjust ? "Recommended adjustment" : "No change needed"}
              </div>
              {lastRec.shouldAdjust && <DeltaDisplay delta={lastRec.delta} />}
              <div style={st.confidenceRow}>
                <span style={st.confidenceLabel}>Confidence</span>
                <span className={`pill ${confidencePill(lastRec.confidence)}`}>
                  {lastRec.confidence}
                </span>
              </div>
              <div style={st.why}>{lastRec.why}</div>
              {lastRec.fallback && <div style={st.fallback}>{lastRec.fallback}</div>}
            </div>
          )}

          {/* Stop history */}
          {session.stops.length > 0 && (
            <div style={{ marginTop: "1.5rem" }}>
              <div className="section-label">Stop history</div>
              {[...session.stops].reverse().map((stop) => (
                <div key={stop.stopNumber} className="card" style={st.stopCard}>
                  <div style={st.stopHeader}>
                    <span style={{ fontWeight: 700, color: "var(--text-strong)" }}>
                      Stop #{stop.stopNumber}
                    </span>
                    <span style={{ color: "var(--text-muted)", fontSize: "0.8125rem" }}>
                      {stop.complaintSlider < 0
                        ? `Tight (${Math.abs(stop.complaintSlider)})`
                        : stop.complaintSlider > 0
                          ? `Loose (${stop.complaintSlider})`
                          : "Neutral"}{" "}
                      · {stop.complaintLocation}
                      {stop.fuel ? ` · ${stop.fuel} gal` : ""}
                    </span>
                    <span className={`pill ${confidencePill(stop.confidence)}`} style={{ marginLeft: "auto" }}>
                      {stop.confidence}
                    </span>
                  </div>
                  <DeltaDisplay delta={stop.recommendedDelta} />
                  {stop.why && <div style={st.stopWhy}>{stop.why}</div>}
                  {stop.feedbackOutcome ? (
                    <span
                      className={`pill ${
                        stop.feedbackOutcome === "better"
                          ? "pill--success"
                          : stop.feedbackOutcome === "worse"
                            ? "pill--danger"
                            : "pill--quiet"
                      }`}
                      style={{ marginTop: "0.5rem" }}
                    >
                      {stop.feedbackOutcome === "better"
                        ? "Better"
                        : stop.feedbackOutcome === "same"
                          ? "Same"
                          : "Worse"}
                    </span>
                  ) : (
                    <div style={st.feedbackRow}>
                      <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>How was it?</span>
                      {["better", "same", "worse"].map((o) => (
                        <button
                          key={o}
                          onClick={() => submitFeedback(stop.stopNumber, o)}
                          className={`pill ${
                            o === "better"
                              ? "pill--success"
                              : o === "worse"
                                ? "pill--danger"
                                : "pill--quiet"
                          }`}
                          style={{ cursor: "pointer" }}
                        >
                          {o.charAt(0).toUpperCase() + o.slice(1)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StateDisplay({ state }: { state: AdjState }) {
  const entries = [
    { label: "LR offset", value: state.lrOffset },
    { label: "RR offset", value: state.rrOffset },
    { label: "LF pressure", value: state.lfPressure },
    { label: "LR pressure", value: state.lrPressure },
  ].filter((e) => e.value !== null && e.value !== undefined);

  if (entries.length === 0)
    return <div style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>Baseline (zero)</div>;

  return (
    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
      {entries.map((e) => (
        <div key={e.label} style={st.statItem}>
          <span style={st.statLabel}>{e.label}</span>
          <span className="mono" style={st.statValue}>
            {e.value! > 0 ? "+" : ""}
            {e.value!.toFixed(3)}"
          </span>
        </div>
      ))}
    </div>
  );
}

function DeltaDisplay({ delta }: { delta: AdjState }) {
  const entries = [
    { label: "LR offset", value: delta.lrOffset },
    { label: "RR offset", value: delta.rrOffset },
    { label: "LF pressure", value: delta.lfPressure },
    { label: "LR pressure", value: delta.lrPressure },
  ].filter((e) => e.value !== null && e.value !== undefined && e.value !== 0);

  if (entries.length === 0)
    return <div style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>No change</div>;

  return (
    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
      {entries.map((e) => (
        <div key={e.label} style={st.statItem}>
          <span style={st.statLabel}>{e.label}</span>
          <span
            className="mono"
            style={{ ...st.statValue, color: e.value! > 0 ? "var(--live)" : "var(--flag-red)" }}
          >
            {e.value! > 0 ? "+" : ""}
            {e.value!.toFixed(3)}"
          </span>
        </div>
      ))}
    </div>
  );
}

const st: Record<string, CSSProperties> = {
  block: { marginBottom: "1rem" },
  chipActive: {
    cursor: "pointer",
    borderColor: "var(--live-edge)",
    color: "var(--live)",
    background: "var(--live-soft)",
  },
  hint: { fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: "0.4rem" },
  profileMeta: { display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", marginTop: "0.6rem" },
  leverHint: { fontSize: "0.8125rem", color: "var(--text-muted)" },
  error: {
    background: "var(--flag-red-soft)",
    borderColor: "var(--flag-red)",
    color: "var(--flag-red)",
    fontSize: "0.82rem",
    marginBottom: "0.85rem",
  },
  sessionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "1rem",
  },
  sessionLabel: { fontSize: "0.82rem", fontWeight: 600, color: "var(--text-default)" },
  btnSm: { padding: "0.3rem 0.7rem", fontSize: "0.8125rem" },
  btnSmDanger: { padding: "0.3rem 0.7rem", fontSize: "0.8125rem", color: "var(--flag-red)" },
  row: { display: "flex", alignItems: "center", gap: "0.85rem" },
  rowLabel: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "var(--text-muted)",
    minWidth: 84,
  },
  sliderLabel: { fontSize: "0.8125rem", color: "var(--text-muted)", textAlign: "center", marginTop: "0.15rem" },
  sliderEnds: { display: "flex", justifyContent: "space-between", fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: "0.2rem" },
  statItem: { display: "flex", flexDirection: "column", gap: "0.15rem" },
  statLabel: { fontFamily: "var(--font-body)", fontSize: "0.8125rem", fontWeight: 500, color: "var(--text-secondary)" },
  statValue: {
    fontSize: "1rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    color: "var(--text-strong)",
  },
  recCard: {
    background: "var(--live-soft)",
    borderColor: "var(--live-edge)",
    marginBottom: "1rem",
  },
  recTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: "var(--live-strong)",
    marginBottom: "0.6rem",
  },
  confidenceRow: { display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.75rem" },
  confidenceLabel: { fontSize: "0.8125rem", color: "var(--text-muted)", fontWeight: 600 },
  why: { fontSize: "0.85rem", color: "var(--text-default)", marginTop: "0.5rem", lineHeight: 1.5 },
  fallback: { fontSize: "0.8125rem", color: "var(--flag-orange)", marginTop: "0.4rem", fontStyle: "italic" },
  stopCard: { marginBottom: "0.5rem" },
  stopHeader: { display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.5rem", fontSize: "0.82rem" },
  stopWhy: { fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: "0.3rem", lineHeight: 1.45 },
  feedbackRow: { display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.6rem", flexWrap: "wrap" },
};
